import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import ErrorMessage from './ErrorMessage';
import { Select, Spin } from 'antd';
import { useFormContext } from 'react-hook-form';
import { CaretDownOutlined, LoadingOutlined } from '@ant-design/icons';
import { mapDataOptions4Select } from 'utils/helpers';
import styled from 'styled-components';
import { normalizePortalText } from 'utils/baochauTheme';
import {
  getSelectValidation,
  getSelectOptionValue,
  normalizeSelectOptions,
  toLabeledSelectValue,
  toLabeledSelectValueList,
  toPlainSelectValue,
} from './selectHelpers';
import { getFormPopupContainer } from './popupHelpers';
import useDeepMemo from 'hooks/useDeepMemo';
import './style.scss';

const SelectStyle = styled(Select)`
  display: flex;

  &&.bw_form_select_control {
    width: 100%;
    height: auto !important;
    min-height: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  .ant-select-selector {
    min-height: 44px !important;
    height: 44px !important;
    padding: 0 14px !important;
    margin: 0 !important;
    border: 1px solid #d6dee9 !important;
    border-radius: 10px !important;
    background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%) !important;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
    align-items: center;
  }

  .ant-select-selector {
    font-size: 14px !important;
    width: 100%;
  }
  .ant-select-arrow .anticon:not(.ant-select-suffix) {
    pointer-events: none;
  }

  .ant-select-arrow {
    right: 14px;
    color: #667085;
  }

  .ant-select-selection-search {
    display: flex;
    align-items: center;
    inset-inline-start: 14px !important;
    inset-inline-end: 34px !important;
  }

  .ant-select-selection-search-input.ant-select-selection-search-input {
    width: 100% !important;
    min-height: 0 !important;
    height: 42px !important;
    padding: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    line-height: 42px !important;
  }

  .ant-select-selection-item {
    padding: 0 !important;
    line-height: 42px !important;
    color: #101828;
  }

  .ant-select-selection-placeholder {
    color: #98a2b3 !important;
    line-height: 42px !important;
  }

  &.is-invalid .ant-select-selector {
    border-color: #fda29b !important;
    box-shadow: 0 0 0 3px rgba(240, 68, 56, 0.1), 0 8px 18px rgba(240, 68, 56, 0.06) !important;
  }

  &.ant-select-focused .ant-select-selector,
  &:hover .ant-select-selector {
    border-color: #f2aaa5 !important;
    box-shadow: 0 0 0 3px rgba(237, 50, 55, 0.1), 0 8px 18px rgba(15, 23, 42, 0.06) !important;
  }

  td & .ant-select-selector,
  .bw_table & .ant-select-selector {
    min-height: 36px !important;
    height: 36px !important;
    padding: 0 10px !important;
    border-radius: 8px !important;
  }

  td & .ant-select-selection-item,
  td & .ant-select-selection-placeholder,
  .bw_table & .ant-select-selection-item,
  .bw_table & .ant-select-selection-placeholder {
    line-height: 34px !important;
    font-size: 13px !important;
  }

  td & .ant-select-selection-search-input,
  .bw_table & .ant-select-selection-search-input {
    height: 34px !important;
    min-height: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  &.bw_salesperson_multi_select.ant-select-multiple {
    .ant-select-selection-overflow-item-suffix {
      flex: 1 1 52px !important;
      min-width: 52px !important;
    }

    .ant-select-selection-search {
      width: 100% !important;
      min-width: 52px !important;
    }

    .ant-select-selection-search-input {
      width: 100% !important;
      cursor: text !important;
    }
  }
`;

const isEmptySelectValue = (value) => value === undefined || value === null || value === '';
const emptyFetchOptions = () => [];

const needsResolveOptionLabel = (value) => {
  if (Array.isArray(value)) {
    return value.some(needsResolveOptionLabel);
  }

  if (isEmptySelectValue(value)) {
    return false;
  }

  if (typeof value !== 'object' || React.isValidElement(value)) {
    return true;
  }

  const primitiveValue = getSelectOptionValue(value);
  const label = value.label ?? value.name ?? value.title;

  return isEmptySelectValue(label) || String(label) === String(primitiveValue);
};

const FormDebouneSelect = ({
  field = '',
  placeholder,
  validation,
  bordered = false,
  fetchOptions = emptyFetchOptions,
  debounceTimeout = 500,
  isLoadingFirst = false,
  noCallApi,
  list,
  className,
  style,
  popupClassName,
  dropdownClassName,
  dropdownStyle,
  classNames,
  styles,
  getPopupContainer,
  onDropdownVisibleChange,
  onOpenChange,
  onChange,
  options: optionsProp,
  mode,
  labelInValue = true,
  autoResolveOption = true,
  ...props
}) => {
  //#region useForm
  const methods = useFormContext();
  const { clearErrors, register, setValue, watch } = methods;
  const isMultiple = mode === 'multiple' || mode === 'tags';
  const stableValidation = useDeepMemo(() => validation, [validation]);
  const selectValidation = useMemo(
    () => getSelectValidation(stableValidation, isMultiple),
    [stableValidation, isMultiple],
  );
  const { error } = methods.getFieldState(field, methods.formState);
  React.useEffect(() => {
    register(field, selectValidation);
  }, [field, register, selectValidation]);
  //#endreigon

  //#region handle deboune
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState(list ?? optionsProp ?? []);
  const fetchOptionsRef = useRef(fetchOptions);
  const latestRequestRef = useRef(0);

  useEffect(() => {
    fetchOptionsRef.current = fetchOptions;
  }, [fetchOptions]);

  const loadOptions = useCallback(
    (value) => {
      if (noCallApi && isEmptySelectValue(value)) {
        return;
      }

      const requestId = ++latestRequestRef.current;
      setLoading(true);
      Promise.resolve()
        .then(() => fetchOptionsRef.current(value))
        .then((newOptions) => {
          if (requestId === latestRequestRef.current) {
            setOptions(mapDataOptions4Select(newOptions));
          }
        })
        .catch(() => {})
        .finally(() => {
          if (requestId === latestRequestRef.current) {
            setLoading(false);
          }
        });
    },
    [noCallApi],
  );

  const handleOpenChange = useCallback(
    (open) => {
      if (open) {
        loadOptions();
      }
      onDropdownVisibleChange?.(open);
      onOpenChange?.(open);
    },
    [loadOptions, onDropdownVisibleChange, onOpenChange],
  );

  useEffect(() => {
    setOptions(list ?? optionsProp ?? []);
  }, [list, optionsProp]);

  useEffect(() => {
    if (isLoadingFirst && options.length === 0) {
      loadOptions();
    }
  }, [isLoadingFirst, loadOptions, options.length]);

  const debounceFetcher = useMemo(() => {
    return debounce(loadOptions, debounceTimeout);
  }, [debounceTimeout, loadOptions]);

  useEffect(() => () => debounceFetcher.cancel(), [debounceFetcher]);
  const selectOptions = useMemo(() => normalizeSelectOptions(options), [options]);
  const popupRootClassName = [
    classNames?.popup?.root,
    'bw_form_debounce_select_dropdown',
    popupClassName,
    dropdownClassName,
  ]
    .filter(Boolean)
    .join(' ');
  const popupRootStyle = {
    zIndex: 'var(--portal-z-dropdown, 1050)',
    ...dropdownStyle,
    ...styles?.popup?.root,
  };
  const fieldValue = watch(field);
  const isLabelInValue = labelInValue !== false;
  const selectedValue = useMemo(() => {
    if (!isLabelInValue) {
      return toPlainSelectValue(fieldValue, isMultiple);
    }

    if (isMultiple) {
      return toLabeledSelectValueList(fieldValue, selectOptions);
    }

    return toLabeledSelectValue(fieldValue, selectOptions);
  }, [fieldValue, isLabelInValue, isMultiple, selectOptions]);

  useEffect(() => {
    if (!autoResolveOption || selectOptions.length > 0 || !needsResolveOptionLabel(fieldValue)) {
      return;
    }

    loadOptions(isMultiple ? undefined : toPlainSelectValue(fieldValue));
  }, [autoResolveOption, fieldValue, isMultiple, loadOptions, selectOptions.length]);

  const handleChange = useCallback(
    (value, selectedOptions) => {
      clearErrors(field);

      if (isEmptySelectValue(value)) {
        setValue(field, isMultiple ? [] : null, { shouldDirty: true, shouldValidate: true });
        onChange?.(value, selectedOptions);
        return;
      }

      if (!isLabelInValue) {
        setValue(field, value, { shouldDirty: true, shouldValidate: true });
        onChange?.(value, selectedOptions);
        return;
      }

      const nextValue = Array.isArray(value)
        ? value.map((item, index) => toLabeledSelectValue({ ...item, ...(selectedOptions?.[index] || {}) }, selectOptions))
        : toLabeledSelectValue({ ...value, ...(selectedOptions || {}) }, selectOptions);

      setValue(field, nextValue ?? null, { shouldDirty: true, shouldValidate: true });
      onChange?.(value, selectedOptions);
    },
    [clearErrors, field, isLabelInValue, isMultiple, onChange, selectOptions, setValue],
  );

  //#endregion
  return (
    <React.Fragment>
      {/* <Tooltip title={methods?.watch(field)}> */}
      <SelectStyle
        {...props}
        style={{
          width: '100%',
          ...style,
        }}
        className={['bw_form_select_control', className, error ? 'is-invalid' : ''].filter(Boolean).join(' ')}
        placeholder={typeof placeholder === 'string' ? normalizePortalText(placeholder) : placeholder}
        mode={mode}
        showSearch
        labelInValue={isLabelInValue}
        suffixIcon={loading ? <LoadingOutlined /> : <CaretDownOutlined />}
        filterOption={false}
        onSearch={debounceFetcher}
        onOpenChange={handleOpenChange}
        getPopupContainer={getPopupContainer || getFormPopupContainer}
        classNames={{
          ...classNames,
          popup: { ...classNames?.popup, root: popupRootClassName },
        }}
        styles={{
          ...styles,
          popup: { ...styles?.popup, root: popupRootStyle },
        }}
        value={selectedValue}
        onChange={handleChange}
        notFoundContent={loading ? <Spin size='small' /> : <p>Không tìm thấy dữ liệu</p>}
        options={selectOptions}
      />
      {/* </Tooltip> */}
      {error && <ErrorMessage message={error?.message} />}
    </React.Fragment>
  );
};

FormDebouneSelect.propTypes = {
  field: PropTypes.string.isRequired,
  validation: PropTypes.object,
  bordered: PropTypes.bool,
  fetchOptions: PropTypes.func,
  debounceTimeout: PropTypes.number,
  autoResolveOption: PropTypes.bool,
};

export default FormDebouneSelect;
